import React, { ReactNode, useCallback } from "react"

import { DropHandler } from "shared/types/DragAndDrop"
import { Floor } from "shared/types/Floor"
import { ProductPosition } from "shared/types/ProductPosition"
import {
  FloorTypename,
  ProductPositionAddonTypename,
  ProductPositionTypename,
  RoomTypename,
  isProjectProductPosition,
  isProjectProductPositionAddon,
  isProjectRoom
} from "shared/types/Project"
import { Ref } from "shared/types/Ref"
import { Room } from "shared/types/Room"
import { TreeNode } from "shared/types/TreeNode"
import { ProductPositionAddon } from "shared/types/__generated__/graphql"
import { DragSource } from "shared/types/drag-and-drop/DragSource"
import { Table } from "shared/ui/table/Table"
import {
  FlattenedTableItemType,
  RowRendererProps,
  TableAPI
} from "shared/ui/table/types"
import { Tooltip } from "shared/ui/tooltip/Tooltip"

import { UNASSIGNED_PRODUCTS_TAB_ID } from "entities/project-children/lib/projectTabIDs"

import { ProductPositionTableLoadingRowPayloadContext } from "./contexts/ProductPositionTableLoadingRowPayloadContext"
import { ProductPositionTableRowPayloadContext } from "./contexts/ProductPositionTableRowPayloadContext"
import { useProductPositionTableViewModel } from "./hooks/useProductPositionTableViewModel"
import { LoadingProductPositionTableRow } from "./rows/loading/LoadingProductPositionTableRow"
import "./styles/_product-position-table-all.scss"
import { getProductPositionTablePositionDepth } from "./utils/getProductPositionTablePositionDepth"

export interface ProductPositionTableProps {
  projectID: string | null | undefined
  activeTabID?: string | null
  projectChildren?:
    | TreeNode<ProductPosition | ProductPositionAddon | Room | Floor>[]
    | null
  tableAPIRef?: Ref<TableAPI>
  topOffset?: number
  isReadonly?: boolean
  isLoading?: boolean
  hideQuantity?: boolean
  showClarificationRequestColumn?: boolean
  columnPreset?: "default" | "selection"
  selectedItems?: string[]
  additiveSelect?: boolean
  rowFocusOnly?: boolean
  showSelectionCheckbox?: boolean
  isSortingDisabled?: boolean
  noDataHint?: ReactNode
  isItemCollapsed?: (item: { id: string; __typename?: string }) => boolean
  pendingProjectChildren?: string[]
  maxDepth?: number
  onItemCollapse?: (id: string) => void
  onPositionChange?: DropHandler
  onSelectionChange?: (ids: string[]) => void
  RowRenderer?: React.ComponentType<
    RowRendererProps<ProductPosition | ProductPositionAddon | Room>
  >
}

const containerProps = {
  id: "product-position-table",
  "aria-owns": "product-position-table--header"
}

const AVAILABLE_DRAG_TYPES = [
  FloorTypename,
  RoomTypename,
  ProductPositionTypename,
  ProductPositionAddonTypename
]

function getProductPositionTableDragType(
  item: TreeNode<ProductPosition | ProductPositionAddon | Room | Floor>
): string {
  return item.__typename || "row"
}

function ProductPositionTableComponent(props: ProductPositionTableProps) {
  const {
    activeTabID,
    productPositions,
    noDataHint,
    isLoading,
    isReadonly,
    selectedItems,
    maxDepth,
    topOffset,
    isSortingDisabled,
    isItemCollapsed,
    onItemCollapse,
    onPositionChange,
    onSelectionChange,
    tableAPIRef,
    getRowCells,
    RowRenderer,
    rowContextValue,
    loadingRowContextValue,
    additiveSelect,
    rowFocusOnly
  } = useProductPositionTableViewModel(props)

  // Create context-aware canInsertInto function
  const canInsertInto = useCallback(
    ({
      source,
      target
    }: {
      target: TreeNode<
        FlattenedTableItemType<
          ProductPosition | ProductPositionAddon | Room | Floor
        >
      > | null
      source: DragSource<unknown>
    }): boolean => {
      if (isProjectRoom(source.data) && target === null) {
        return true
      }
      if (isProjectProductPosition(source.data) && isProjectRoom(target)) {
        return true
      }
      if (
        activeTabID === UNASSIGNED_PRODUCTS_TAB_ID &&
        isProjectProductPosition(source.data) &&
        target === null
      ) {
        return true
      }
      return false
    },
    [activeTabID]
  )

  const canDrag = useCallback(
    (item: TreeNode<ProductPosition | ProductPositionAddon | Room | Floor>) =>
      !isProjectProductPositionAddon(item),
    []
  )

  return (
    <div
      className="product-position-table"
      data-testid="product-position-table"
    >
      <Tooltip.Provider>
        <ProductPositionTableRowPayloadContext.Provider value={rowContextValue}>
          <ProductPositionTableLoadingRowPayloadContext.Provider
            value={loadingRowContextValue}
          >
            <Table
              RowRenderer={RowRenderer}
              topOffset={topOffset}
              tableAPIRef={tableAPIRef}
              maxDepth={maxDepth}
              items={productPositions}
              noDataHint={noDataHint}
              selectedItems={selectedItems}
              isLoading={isLoading}
              isReadonly={isReadonly}
              isDragDisabled={isSortingDisabled}
              canDrag={canDrag}
              hideDefaultDragPreview
              LoadingRowRenderer={LoadingProductPositionTableRow}
              multiSelect
              additiveSelect={additiveSelect}
              rowFocusOnly={rowFocusOnly}
              onItemPositionChange={onPositionChange}
              onSelectedItemsChange={onSelectionChange}
              isItemCollapsed={isItemCollapsed}
              onItemCollapse={onItemCollapse}
              getDepth={getProductPositionTablePositionDepth}
              canInsertInto={canInsertInto}
              getDragType={getProductPositionTableDragType}
              availableDragTypes={AVAILABLE_DRAG_TYPES}
              rowHeight={44}
              getRowCells={getRowCells}
              containerProps={containerProps}
            />
          </ProductPositionTableLoadingRowPayloadContext.Provider>
        </ProductPositionTableRowPayloadContext.Provider>
      </Tooltip.Provider>
    </div>
  )
}

export const ProductPositionTable = React.memo(ProductPositionTableComponent)
